/**
 * VLE Consent Table Component
 *
 * Displays VLE consent records with 9 columns:
 * Request ID | Timestamp | Mobile | PAN | Consent Status | Data Status | Payment Status | Pay | Share
 *
 * Uses Equal DS Table component for consistent styling with ConsentRequest page.
 *
 * @author Claude Code
 * @date February 2026
 * @doc Reference: Doc 32 - Phase 2 Implementation
 */

import React, { useMemo } from 'react';
import { Table, Button, type TableColumn } from 'equal-ds-ui';
import { CreditCard, Share2, RefreshCw, Undo2 } from 'lucide-react';
import {
  type VleConsentTableProps,
  type VleConsentRecord,
  CONSENT_STATUS_CONFIG,
  DATA_STATUS_CONFIG,
  PAYMENT_STATUS_CONFIG,
  PAGE_SIZE_OPTIONS,
} from './types';

/**
 * Derive consent status tag with color
 */
const deriveConsentStatusTag = (status?: string | null) => {
  if (!status) return '-';

  const normalized = status.toUpperCase();
  const config = CONSENT_STATUS_CONFIG[normalized] || {
    label: status,
    className: 'bg-gray-100 text-gray-800 border-gray-300',
  };

  return {
    type: 'tag' as const,
    props: {
      children: config.label,
      className: config.className,
    },
  };
};

/**
 * Derive data status from consent_data_fetches
 * Returns 'ACTIVE' only when data_life_status is ACTIVE AND has_transactions is true
 * Otherwise returns null (NOT AVAILABLE)
 */
const deriveDataStatus = (consent: VleConsentRecord): string | null => {
  if (consent.consent_data_fetches && consent.consent_data_fetches.length > 0) {
    const fetch = consent.consent_data_fetches[0];
    if (fetch?.data_life_status === 'ACTIVE' && fetch?.has_transactions) {
      return 'ACTIVE';
    }
  }
  return null;
};

/**
 * Derive data status tag with color
 * Only 2 states: AVAILABLE (green) or NOT AVAILABLE (gray)
 */
const deriveDataStatusTag = (consent: VleConsentRecord) => {
  const status = deriveDataStatus(consent);

  if (status === 'ACTIVE') {
    const config = DATA_STATUS_CONFIG.AVAILABLE;
    return {
      type: 'tag' as const,
      props: {
        children: config.label,
        className: config.className,
      },
    };
  }

  const config = DATA_STATUS_CONFIG.NOT_AVAILABLE;
  return {
    type: 'tag' as const,
    props: {
      children: config.label,
      className: config.className,
    },
  };
};

/**
 * Format date for display
 */
const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};


export const VleConsentTable: React.FC<VleConsentTableProps> = ({
  consents,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRefresh,
  onPaymentClick,
  onShareClick,
  onRefreshPaymentStatus,
  refreshingConsentId,
  onReversePayment,
  reversingConsentId,
}) => {
  /**
   * Table columns definition
   */
  const columns: TableColumn<VleConsentRecord>[] = useMemo(
    () => [
      {
        key: 'consent_handle',
        header: 'Request ID',
        width: 380,
        accessor: (row) => row.consent_handle ?? '-',
        copyable: true,
      },
      {
        key: 'created_at',
        header: 'Timestamp',
        width: 180,
        render: (consent) => (
          <span className="text-sm text-gray-600 whitespace-nowrap">
            {formatDate(consent.created_at)}
          </span>
        ),
      },
      {
        key: 'mobile',
        header: 'Mobile',
        width: 120,
        render: (consent) => (
          <span className="font-medium">
            {/* PRD: 10-digit mobile number (no masking) */}
            {consent.identifier_value || '-'}
          </span>
        ),
      },
      {
        key: 'pan',
        header: 'PAN',
        width: 120,
        render: (consent) => (
          <span className="font-mono text-sm">
            {/* PRD line 119: PAN (partially masked) - format: XXXXX4321F */}
            {consent.masked_pan || '-'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Consent',
        width: 110,
        render: (consent) => deriveConsentStatusTag(consent.status),
      },
      {
        key: 'data_status',
        header: 'Data',
        width: 110,
        render: (consent) => deriveDataStatusTag(consent),
      },
      {
        key: 'payment_status',
        header: 'Payment Status',
        width: 210,
        render: (consent) => {
          const paymentStatus = consent.payment_status || 'NOT_INITIATED';
          const config = PAYMENT_STATUS_CONFIG[paymentStatus] || PAYMENT_STATUS_CONFIG.NOT_INITIATED;
          const showRefresh = paymentStatus === 'PENDING' || paymentStatus === 'UNKNOWN';
          const showReverse = paymentStatus === 'UNKNOWN';
          const isRefreshing = refreshingConsentId === consent.consent_id;
          const isReversing = reversingConsentId === consent.consent_id;

          return (
            <span className="inline-flex items-center gap-1.5">
              <span className={`px-2 py-1 rounded text-xs font-medium border ${config.className}`}>
                {config.label}
              </span>
              {showRefresh && (
                <button
                  onClick={() => onRefreshPaymentStatus(consent)}
                  disabled={isRefreshing || isReversing}
                  className={`p-1.5 rounded-md border transition-all duration-200 ${
                    isRefreshing
                      ? 'bg-blue-50 border-blue-300 cursor-wait'
                      : 'bg-white border-gray-300 hover:bg-blue-50 hover:border-blue-400 hover:shadow-sm active:scale-95'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title="Check payment status"
                >
                  <RefreshCw size={15} className={isRefreshing ? 'animate-spin text-blue-600' : 'text-blue-600'} />
                </button>
              )}
              {showReverse && (
                <button
                  onClick={() => onReversePayment(consent)}
                  disabled={isReversing || isRefreshing}
                  className={`p-1.5 rounded-md border transition-all duration-200 ${
                    isReversing
                      ? 'bg-orange-50 border-orange-300 cursor-wait'
                      : 'bg-white border-gray-300 hover:bg-orange-50 hover:border-orange-400 hover:shadow-sm active:scale-95'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title="Reverse payment"
                >
                  <Undo2 size={15} className={isReversing ? 'animate-spin text-orange-600' : 'text-orange-600'} />
                </button>
              )}
            </span>
          );
        },
      },
      {
        key: 'pay',
        header: 'Pay',
        width: 100,
        render: (consent) => {
          const dataStatus = deriveDataStatus(consent);
          const paymentStatus = consent.payment_status || 'NOT_INITIATED';
          // Enable Pay only if consent=ACTIVE, data=ACTIVE, and payment NOT in a terminal/uncertain state
          // AIDEV-NOTE: Pay disabled for UNKNOWN (000) — money may have been deducted, VLE must
          // refresh or reverse first. Also disabled for REVERSED, REVERSAL_DECLINED. See Doc 63.
          const isEnabled = consent.status === 'ACTIVE' && dataStatus === 'ACTIVE'
            && paymentStatus !== 'SUCCESS' && paymentStatus !== 'PENDING'
            && paymentStatus !== 'UNKNOWN' && paymentStatus !== 'REVERSED'
            && paymentStatus !== 'REVERSAL_DECLINED';

          return (
            <Button
              variant={isEnabled ? 'primary' : 'secondary'}
              size="sm"
              disabled={!isEnabled}
              onClick={() => isEnabled && onPaymentClick(consent)}
              className="text-xs"
            >
              <CreditCard size={14} className="mr-1" />
              Pay
            </Button>
          );
        },
      },
      {
        key: 'share',
        header: 'Share',
        width: 120,
        render: (consent) => {
          // AIDEV-NOTE: Share enabled when Payment=SUCCESS and Data=ACTIVE (Phase 5)
          const dataStatus = deriveDataStatus(consent);
          const paymentStatus = consent.payment_status || 'NOT_INITIATED';
          const isShareEnabled = paymentStatus === 'SUCCESS' && dataStatus === 'ACTIVE';

          return (
            <Button
              variant={isShareEnabled ? 'primary' : 'secondary'}
              size="sm"
              disabled={!isShareEnabled}
              onClick={() => isShareEnabled && onShareClick(consent)}
              className="text-xs"
              title={isShareEnabled ? 'Send statement via SMS' : 'Pay first to enable sharing'}
            >
              <Share2 size={14} className="mr-1" />
              Share
            </Button>
          );
        },
      },
    ],
    [onPaymentClick, onShareClick, onRefreshPaymentStatus, refreshingConsentId, onReversePayment, reversingConsentId]
  );

  /**
   * Transform data for Equal DS Table
   */
  const tableData = useMemo(() => {
    return consents.map((consent) => ({
      ...consent,
      _id: consent.id.toString(),
    }));
  }, [consents]);

  return (
    <div className="equal-ds-root">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {totalCount > 0 ? (
            <>
              Showing {(currentPage - 1) * pageSize + 1} -{' '}
              {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
            </>
          ) : (
            'No records found'
          )}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={onRefresh}
          disabled={isLoading}
        >
          <RefreshCw size={14} className={`mr-1 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Table
        columns={columns}
        data={tableData}
        pagination={{
          currentPage,
          pageSize,
          totalItems: totalCount,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onPageChange,
          onPageSizeChange,
          showPageSizeSelector: true,
          isFetching: isLoading,
        }}
      />
    </div>
  );
};

export default VleConsentTable;
